import React, { useEffect, useState } from "react";
import AdminSideBar from "../../layouts/AdminSideBar";
import LoadingPage from "../../layouts/Loading";
import { AlertTriangle, CheckCircle } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import "../css/Orders.css";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [filter, setFilter] = useState("all"); // "all" | "cod" | "card"

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await fetch("http://127.0.0.1:8000/medicine/admin/orders/");
      const data = await res.json();
      setOrders(data.orders || []);

      const pending = (data.orders || []).filter(
        (o) => o.status === "Pending"
      ).length;
      setPendingOrdersCount(pending);
    } catch (err) {
      setErrorMsg("Failed to fetch orders!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // ---------------- FILTER ORDERS ----------------
  // ---------------- FILTER ORDERS ----------------
  const filteredOrders = orders.filter((o) => {
    if (filter === "all") return true;
    if (filter === "cod") return o.payment_method === "cod";
    if (filter === "card") return o.payment_method === "card";
    if (filter === "completed")
      return o.status === "Completed" || o.status === "Delivered";
    return true;
  });

  // Table ke liye:
  const pendingOrders = filteredOrders.filter((o) => o.status === "Pending");
  const completedOrders = filteredOrders.filter(
    (o) => o.status === "Completed" || o.status === "Delivered"
  );

  // ---------------- PDF FUNCTION ----------------
  const downloadPDF = () => {
    if (!orders.length) {
      alert("No orders found!");
      return;
    }

    const doc = new jsPDF();
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("MediCare Store", 14, 15);

    doc.setFontSize(12);
    doc.text("Order History Report", 14, 25);
    doc.line(14, 28, 195, 28);

    autoTable(doc, {
      startY: 35,
      theme: "grid",
      headStyles: { fillColor: [30, 144, 255] },
      head: [
        [
          "ID",
          "Name",
          "Email",
          "Amount",
          "Payment Method",
          "Status",
          "Created At",
        ],
      ],
      body: filteredOrders.map((o) => [
        o.id ?? "-",
        o.name ?? "-",
        o.email ?? "-",
        `Rs ${Number(o.total_amount || 0).toLocaleString("en-PK", {
          minimumFractionDigits: 2,
        })}`,
        o.payment_method === "cod" ? "Cash on Delivery" : "Card Payment",
        o.status ?? "-",
        o.created_at ? String(o.created_at).slice(0, 10) : "-",
      ]),
    });

    const Y = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(12);
    doc.text("Admin Report - MediCare Store", 14, Y);

    doc.setFontSize(10);
    doc.text("Generated by: Admin", 14, Y + 6);
    doc.text("Contact: admin@medicare.pk | +92-342-0339016", 14, Y + 12);
    doc.text("Address: Main Road, City Center, Pakistan", 14, Y + 18);

    doc.setFontSize(9);
    doc.text("© 2025 MediCare Store", 14, Y + 25);

    doc.save("Orders_Admin_Report.pdf");
  };

  // ---------------- UPDATE & DELETE ----------------
  const updateOrderStatus = async (id, status) => {
    try {
      setActionLoading(true);
      await fetch(`http://127.0.0.1:8000/medicine/admin/orders/update/${id}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      setSuccessMsg("Order status updated!");
      setTimeout(() => setSuccessMsg(""), 3000);
      fetchOrders();
    } catch (err) {
      setErrorMsg("Failed to update order!");
    } finally {
      setActionLoading(false);
    }
  };

  const deleteOrder = async (id) => {
    try {
      setActionLoading(true);
      await fetch(`http://127.0.0.1:8000/medicine/admin/orders/delete/${id}/`, {
        method: "DELETE",
      });
      setSuccessMsg("Order deleted successfully!");
      setTimeout(() => setSuccessMsg(""), 3000);
      fetchOrders();
    } catch (err) {
      setErrorMsg("Failed to delete order!");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <LoadingPage />;

  return (
    <div className="admin-panel">
      <AdminSideBar pendingOrdersCount={pendingOrdersCount} />

      <div className="main-panel">
        <div className="content">
          <div className="max-width-container">
            <h1 className="page-title">All Orders</h1>

            {/* ---------------- FILTER SELECT ---------------- */}
            <div className="filter-wrapper">
              <label htmlFor="paymentFilter">Filter Orders: </label>
              <select
                id="paymentFilter"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Orders</option>
                <option value="cod">Cash on Delivery</option>
                <option value="card">Card Payment</option>
                <option value="completed">Completed Orders</option>
              </select>
            </div>

            <button onClick={downloadPDF} className="download-btn">
              Download Orders PDF
            </button>

            {errorMsg && (
              <div className="message-box message-box-error">
                <AlertTriangle className="message-icon" /> {errorMsg}
              </div>
            )}
            {successMsg && (
              <div className="message-box message-box-success">
                <CheckCircle className="message-icon" /> {successMsg}
              </div>
            )}

            {actionLoading && (
              <p className="updating-msg">Updating... Please Wait</p>
            )}

            {/* ---------------- PENDING ORDERS TABLE ---------------- */}
            {pendingOrders.length > 0 && (
              <>
                <h2 className="table-title">Pending Orders</h2>
                <div className="table-card">
                  <table className="styled-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Amount</th>
                        <th>Payment Method</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingOrders.map((o) => (
                        <tr key={o.id}>
                          <td>{o.id}</td>
                          <td>{o.name}</td>
                          <td>{o.email}</td>
                          <td>{o.total_amount}</td>
                          <td>
                            {o.payment_method === "cod"
                              ? "Cash on Delivery"
                              : "Card Payment"}
                          </td>
                          <td>
                            <select
                              value={o.status}
                              onChange={(e) =>
                                updateOrderStatus(o.id, e.target.value)
                              }
                              disabled={actionLoading}
                              className="form-select"
                            >
                              <option value="Pending">Pending</option>
                              <option value="Completed">Completed</option>
                            </select>
                          </td>
                          <td>
                            <button
                              onClick={() => deleteOrder(o.id)}
                              disabled={actionLoading}
                              className="delete-btn"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* ---------------- COMPLETED ORDERS TABLE ---------------- */}
            {completedOrders.length > 0 && (
              <>
                <h2 className="table-title">Completed Orders</h2>
                <div className="table-card">
                  <table className="styled-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Amount</th>
                        <th>Payment Method</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completedOrders.map((o) => (
                        <tr key={o.id}>
                          <td>{o.id}</td>
                          <td>{o.name}</td>
                          <td>{o.email}</td>
                          <td>{o.total_amount}</td>
                          <td>
                            {o.payment_method === "cod"
                              ? "Cash on Delivery"
                              : "Card Payment"}
                          </td>
                          <td>
                            <select
                              value={o.status}
                              disabled
                              className="form-select"
                            >
                              <option value={o.status}>{o.status}</option>
                            </select>
                          </td>
                          <td>
                            <button
                              onClick={() => deleteOrder(o.id)}
                              disabled={actionLoading}
                              className="delete-btn"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
